<script setup>
import {computed, inject, reactive, ref} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog, Checkbox} from "primevue";
import {Icon} from "@iconify/vue";
//import stores
import {useMarketMapStore} from "@/stores/marketMap.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";

//stores
const marketMapStore = useMarketMapStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const toast = useToast();

//emits

//props
const props = defineProps({
  selectedItem: {
    type: Object,
    required: true
  },
  selectedStore: {
    type: Object,
    required: true
  }
});

//injects
const visible = inject('isShowChangePriceModal')

//component variables
const form = reactive({
  id: props.selectedItem.offerID,
  store: props.selectedItem.store,
  price: null,
  wholesalePrice: null,
  wholesaleQuantity: 2000
})

const game = props.selectedStore.game;
const wholesaleEnabled = ref(false)
const isWholesaleAvailable = computed(() => game === 'WoW Classic Era')

const rules = computed(() => {
  return {
    price: {required},
    wholesalePrice: isWholesaleAvailable.value && form.wholesaleQuantity ? {required} : {},
    wholesaleQuantity: isWholesaleAvailable.value && form.wholesalePrice ? {required} : {},
  };
});
const $vuelidate = useVuelidate(rules, form);

//global functions

//component functions
const onSave = async () => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  try {
    saving.value = true
    const res = await marketMapStore.action_changeOfferPrice({
      ...form,
      "game": game,
      ...(isWholesaleAvailable.value && {
        "wholesalePrice": wholesaleEnabled.value ? form.wholesalePrice ? form.wholesalePrice : -1 : null,
        "wholesaleQuantity": wholesaleEnabled.value ? form.wholesaleQuantity ? form.wholesaleQuantity : -1 : null
      })
    })
    if (res.result === true) {
      showMessage('Price Changed.', 'success');
      setTimeout(() => {
        visible.value = false
      }, 1000)
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

const calWholesalePrice = (event) => {
  const price = event.target.value;

  if (price !== '') {
    const decimalPlaces = price.split('.')[1]?.length || 0
    const precision = Math.max(decimalPlaces, 5)

    form.wholesalePrice = Number((Number(price) - 0.00001).toFixed(precision))
  } else {
    form.wholesalePrice = null
  }
}

//hooks

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Change Price
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <FloatLabel variant="on">
        <InputText id="title" :value="selectedItem.price" autocomplete="off" readonly
                   disabled
                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm p-filled !border-slate-800"/>
        <label for="title" class="!bg-slate-900 !text-slate-500">Current Price</label>
      </FloatLabel>
      <div class="flex flex-col gap-0.5">
        <FloatLabel variant="on">
          <InputText v-allow-digits-dot id="price" v-model="form.price" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                     @input="calWholesalePrice"/>
          <label for="price" class="!bg-slate-900">New Price</label>
        </FloatLabel>
        <template v-if="$vuelidate.price.$error">
          <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
             v-if="!$vuelidate.price.required.$response">
            This field is required
          </p>
        </template>
      </div>
      <div v-if="isWholesaleAvailable" class="flex items-center gap-3">
        <div class="grid flex-1 grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="flex flex-col gap-0.5">
            <FloatLabel variant="on">
              <InputText v-allow-digits-dot id="wholesalePrice" v-model="form.wholesalePrice" autocomplete="off"
                         :disabled="!wholesaleEnabled"
                         class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
              <label for="wholesalePrice" class="!bg-slate-900" :class="!wholesaleEnabled ? '!text-slate-500' : ''">Wholesale
                Price</label>
            </FloatLabel>
            <template v-if="$vuelidate.wholesalePrice.$error">
              <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
                 v-if="!$vuelidate.wholesalePrice.required.$response">
                This field is required
              </p>
            </template>
          </div>
          <div class="flex flex-col gap-0.5">
            <FloatLabel variant="on">
              <InputText v-allow-digits id="wholesaleQuantity" v-model="form.wholesaleQuantity" autocomplete="off"
                         :disabled="!wholesaleEnabled"
                         class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
              <label for="wholesaleQuantity" class="!bg-slate-900" :class="!wholesaleEnabled ? '!text-slate-500' : ''">Wholesale
                Quantity</label>
            </FloatLabel>
            <template v-if="$vuelidate.wholesaleQuantity.$error">
              <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
                 v-if="!$vuelidate.wholesaleQuantity.required.$response">
                This field is required
              </p>
            </template>
          </div>
        </div>
        <Checkbox v-model="wholesaleEnabled" input-id="wholesaleEnabled" binary/>
      </div>
    </div>

    <div class="flex justify-end gap-3 select-none">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Change Price
      </button>
    </div>
  </Dialog>
</template>
<style></style>
